import { Link } from 'react-router-dom';
import { useI18n, formatDay } from '../../i18n';
import BoutLevelPill from '../bout/BoutLevelPill';
import { QualityBlock } from './OfficialTimeline';
import { OwnTeamTable, PanelTable, STAT_TILE_CLS, StatTile } from './QualityTables';
import { ScaleIcon, WarningIcon } from '../shared/Icons';
import { personName } from '../../utils/personName';
import { rate } from '../../utils/officialStats';
import type { OfficialsReport, OfficialsReviewRow, ReportCoverage, ReportPerson } from '../../types';

// #647 — the report of the officials of one tournament: the views of the
// Summary modal of the Officials tab (the plan on #647). Each view reuses a
// part of the page of an official: the block of "Over time", the tables of
// the panel and the own-team cells, the level pill and the marks of a bout.
// The server sends the report only to canSeeAllOfficialStats.

/** The quality numbers of each official in this tournament, under the totals
 *  of all the judges of the tournament. */
export function ReportQuality({ report }: { report: OfficialsReport }) {
  const { t } = useI18n();
  if (!report.officials.length) return <p className="text-sm text-gray-400 text-center py-6">{t.officials.noSeats}</p>;
  const field = report.field;
  return (
    <div className="space-y-3" data-testid="officials-report-quality">
      <p className="text-xs text-gray-500">{t.officials.reportQualityHint}</p>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <PanelTable testId="officials-report-panel" title={t.officials.reportAllJudges} cells={field.panel} />
        <OwnTeamTable testId="officials-report-own-team" title={t.officials.reportAllJudges} cells={field.team} />
      </div>
      <ul className="divide-y divide-gray-100 border-t border-gray-100 text-xs">
        {report.officials.map(o => (
          <QualityBlock key={o.user_id} title={personName(o.first_name, o.last_name)} href={`/officials/${o.user_id}`} date={null}
            counts={o.seats} levels={o.levels} quality={{ ...o.quality, field }}
            testId="officials-report-block" rowAttrs={{ 'data-official-id': o.user_id }} />
        ))}
      </ul>
    </div>
  );
}

const name = (p: ReportPerson | null) => (p ? personName(p.first_name, p.last_name) : '—');

/** One bout to review: the bout, then a mark for each reason. The marks use
 *  the classes and the texts of the marks of the page of an official. */
function ReviewRow({ r }: { r: OfficialsReviewRow }) {
  const { t } = useI18n();
  const round = (n: number | null) => (n === null ? t.officials.pillBout : `R${n}`);
  const warn = 'badge gap-1 bg-amber-50 text-amber-800 max-w-full whitespace-normal text-left';
  const icon = <WarningIcon className="w-3.5 h-3.5 shrink-0" />;
  const againstPanel = [...r.rounds_against_panel.map(round), ...(r.bout_against_panel ? [t.officials.pillBout] : [])];
  const own = (side: 'for_own' | 'against_own') => r.own_team.filter(o => o.side === side)
    .map(o => `${round(o.round)} (${name(o.referee)})`).join(', ');
  const forOwn = own('for_own');
  const againstOwn = own('against_own');
  return (
    <li data-testid="officials-review-row" data-bout-id={r.bout_id} className="py-2.5 space-y-1.5">
      <div className="flex items-start justify-between gap-3">
        <Link to={`/bouts/${r.bout_id}`} className="font-medium text-sm min-w-0 break-words hover:underline">
          #{r.bout_number} · {personName(r.red.first_name, r.red.last_name)} — {personName(r.blue.first_name, r.blue.last_name)}
        </Link>
        <span className="text-xs text-gray-500 shrink-0 max-w-[45%] text-right">
          {[r.ring_name, r.bout_date ? formatDay(r.bout_date, 'dd MMM') : null].filter(Boolean).join(' · ')}
        </span>
      </div>
      <div className="flex flex-wrap items-center gap-1.5 text-xs">
        {againstPanel.length > 0 && (
          <span data-testid="review-against-panel" data-rounds={r.rounds_against_panel.join(',')} data-bout={String(r.bout_against_panel)}
            className="badge gap-1 bg-sky-50 text-sky-800 max-w-full whitespace-normal text-left">
            <ScaleIcon className="w-3.5 h-3.5 shrink-0" />{t.officials.pillAgainstPanel.replace('{what}', againstPanel.join(', '))}
          </span>
        )}
        {r.bout_split && <span data-testid="review-split" className="badge badge-gray whitespace-nowrap">{t.officials.pillSplit}</span>}
        {r.rounds_total_differs.length > 0 && (
          <span data-testid="review-jury-total" data-rounds={r.rounds_total_differs.join(',')} className={warn}>
            {icon}{t.officials.pillJury.replace('{rounds}', `${r.rounds_total_differs.map(round).join(', ')} (${name(r.jury)})`)}
          </span>
        )}
        {r.dropped.length > 0 && (
          <span data-testid="review-dropped" data-rounds={r.dropped.map(d => d.round).join(',')} className={warn}>
            {icon}{t.officials.pillDropped.replace('{rounds}', r.dropped.map(d => `${round(d.round)} (${name(d.referee)})`).join(', '))}
          </span>
        )}
        {forOwn && <span data-testid="review-own-team" data-side="for_own" className={warn}>{icon}{t.officials.pillForOwn.replace('{what}', forOwn)}</span>}
        {againstOwn && <span data-testid="review-own-team" data-side="against_own" className={warn}>{icon}{t.officials.pillAgainstOwn.replace('{what}', againstOwn)}</span>}
      </div>
    </li>
  );
}

/** The scored bouts with a reason to review, in the order of the bouts. */
export function ReportReview({ report }: { report: OfficialsReport }) {
  const { t } = useI18n();
  return (
    <div className="space-y-2" data-testid="officials-report-review" data-count={report.review.length}>
      <p className="text-xs text-gray-500">{t.officials.reviewHint}</p>
      {report.review.length === 0
        ? <p className="text-sm text-gray-400 text-center py-6" data-testid="officials-review-none">{t.officials.reviewNone}</p>
        : <ul className="divide-y divide-gray-100 border-t border-gray-100">{report.review.map(r => <ReviewRow key={r.bout_id} r={r} />)}</ul>}
    </div>
  );
}

/** The Pro and the High bouts and their ring referees, and the neutral panels. */
export function ReportCoverageView({ report }: { report: OfficialsReport }) {
  const { t } = useI18n();
  const tile = (level: 'pro' | 'high', c: ReportCoverage) => (
    <div data-testid={`officials-coverage-${level}`} data-bouts={c.bouts} data-ifma={c.ifma} data-novice={c.novice} data-other={c.other} data-none={c.no_ring_referee}
      className={`${STAT_TILE_CLS} space-y-1.5`}>
      <div className="flex items-center justify-between gap-2">
        <BoutLevelPill level={level} />
        <span className="text-lg font-black tabular-nums">{c.bouts}</span>
      </div>
      <dl className="grid grid-cols-[1fr_auto] gap-x-3 gap-y-0.5 text-xs">
        <dt className="text-gray-500">{t.officials.coverageIfma}</dt>
        <dd className="font-semibold tabular-nums text-right">{c.ifma}</dd>
        <dt className="text-gray-500">{t.officials.coverageNovice}</dt>
        <dd className={`font-semibold tabular-nums text-right ${c.novice > 0 ? 'text-amber-800' : ''}`}>{c.novice}</dd>
        {c.novice_bouts.length > 0 && (
          <dd className="col-span-2 text-gray-500" data-testid={`officials-coverage-${level}-novice-bouts`}>
            {t.officials.coverageBouts.replace('{list}', c.novice_bouts.map(n => `#${n}`).join(', '))}
          </dd>
        )}
        <dt className="text-gray-500">{t.officials.coverageOther}</dt>
        <dd className="font-semibold tabular-nums text-right">{c.other}</dd>
        <dt className="text-gray-500">{t.officials.coverageNoRef}</dt>
        <dd className="font-semibold tabular-nums text-right">{c.no_ring_referee}</dd>
      </dl>
    </div>
  );
  const n = report.neutrality;
  const neutral = rate(n.neutral, n.counted);
  return (
    <div className="space-y-3" data-testid="officials-report-coverage">
      <p className="text-xs text-gray-500">{t.officials.coverageHint}</p>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {tile('pro', report.coverage.pro)}
        {tile('high', report.coverage.high)}
      </div>
      <StatTile testId="officials-neutrality" attrs={{ 'data-counted': n.counted, 'data-neutral': n.neutral }} strongTitle
        title={t.officials.neutralityTitle} value={neutral === null ? '—' : `${neutral}%`}
        detail={t.officials.neutralityValue.replace('{n}', String(n.neutral)).replace('{m}', String(n.counted))}
        help={t.officials.neutralityHint} />
    </div>
  );
}
